import { h, Component } from 'preact';
import { Spinner } from '@cube/blocks';
import style from './style';
import cx from 'classnames';
import debounce from '@os/debounce';

const THIRTY_FPS = 33.33;

export const FinanceSpinner = (props) => (
	<div class={cx(style.loading, props.class)} style={props.style}>
		<div>
			<Spinner showError class={style.spinner} />
		</div>
	</div>
);

function safeGetBBox(element) {
	try {
		return element.getBBox();
	}
	//firefox throws exception if you try to getBBox() of a hidden element
	//silently ignore it
	catch (e) {}
}

/**
 * An SVG pill that auto-sizes/centers based on the text in the label
 */
export class Pill extends Component {

	state = { width: 1, height: 1 };

	/**
	 * Update the width state with the new width of the pill
	 */
	updateWidth = debounce(() => {
		let { maxWidth=99999 } = this.props;
		let dims = safeGetBBox(this.base && this.base.lastChild);

		// failed to find dimensions
		if (!dims) return;
		let { width, height } = dims;

		//pad the width for the rounded corners but don't exceed the maxWidth
		width = Math.min(width+height, maxWidth);
		this.setState({ width, height });

	}, THIRTY_FPS);

	static defaultProps = {
		padding: { top: 0, bottom: 0, left: 0, right: 0 },
		minX: -1,
		maxX: 999999,
		pillShape: 'rounded'
	};

	//update the width based on the size of the newly created text svg node
	componentDidMount() {
		this.updateWidth();
	}

	//update the width only if the label property or maxWidth has changed
	componentDidUpdate({ label, maxWidth }) {
		if (label !== this.props.label || maxWidth !== this.props.maxWidth) {
			this.updateWidth();
		}
	}

	/**
	 * @param x The upper-left x dimension of where to position the pill (see center* params below for more positioning)
	 * @param y The upper-left y dimension of where to position the pill (see center* params below for more positioning)
	 * @param {Object} padding object witih top/bottom/left/right keys for the amount of padding to add around the text
	 * @param maxWidth The maximum width in pixels of the pill
	 * @param label The label/text to be shown in the pill
	 * @param centerX True/False whether to center the pill horizontally along the x-axis.  Default: False.
	 * @param centerY True/False whether to center the pill vertically along the y-axis.  Default: False.
	 * @param mixX If specified, the minimum x-value that the pill should be drawn at
	 * @param maxX If specified, the maximum x-value that the pill should be drawn at
	 * @param class The class attribute to be applied to the surrounding <g> element for the pill
	 * @param pillShape allows for different shaped pills. Defult: rounded
	 *
	 */
	render({ x, y, padding, maxWidth, label, centerX, centerY, minX, maxX, pillShape, ...restProps }, { width, height })  {

		const { top=0, bottom=0, left=0, right=0 } = padding;

		//Make sure we still render something valid even if we don't get valid x,y
		//so that preact will re-render the SVG component when we do get valid props
		const shouldHide = (typeof x === 'undefined' || typeof y === 'undefined');
		if (shouldHide) {
			x=0;
			y=0;
		}

		height = height + top + bottom;
		width = width + left + right;
		let halfHeight = height/2;
		let halfWidth = width/2;
		let pointyPillWidth = Math.max((width - 7), 0);
		if (centerX) x -= halfWidth;
		let rectX =  Math.min(maxX-width, Math.max(minX, x));
		if (centerY) y -= halfHeight;

		//creates a polygon for the pointy pill pointer
		let polyPoints = String('8,0 ' + halfWidth + ',0 ' + halfWidth + ',' + height + ' 8,' + height + ' 0,' + halfHeight);

		if (pillShape === 'rounded' || !pillShape) {
			return (
				<g style={shouldHide ? 'display:none' : false} {...restProps} >
					<rect x={rectX} y={y} width={width} height={height} rx={halfHeight} ry={halfHeight} />
					<text x={rectX + halfWidth + left} y={y + halfHeight + top/2} dy="0.38em" text-anchor="middle" >{label}</text>
				</g>
			);
		}

		if (pillShape === 'pointy') {
			return (
				<g style={shouldHide ? 'display:none' : false} {...restProps} >
					<svg x={rectX - 1} y={y} height={height + 2} width={halfWidth} >
						<polygon points={polyPoints} />
					</svg>
					<rect x={rectX + 6} y={y} width={pointyPillWidth} height={height} rx={halfHeight} ry={halfHeight} />
					<text x={(rectX + halfWidth + left) + 1} y={y + halfHeight + top/2} dy="0.38em" text-anchor="middle" >{label}</text>
				</g>
			);
		}
	}
}
